import { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import { AkamCheckIcon, AkamLogoCheckIcon, AkamLogoTextIcon, HamburgerIcon, LoginIcon } from 'icons';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal, resetRedirectUrl } from 'modules/auth';
import { globalConst, onTrackYandexGoal, useScrollHeader } from 'modules/global';

import { Feedback, Visible } from 'components';

import LanguageDropdown from './language-dropdown';
import MobileDrawer from './mobile-drawer';
import ProfileDropDown from './profile-dropdown';

type NavItemType = {
  label: string;
  href: string;
  isNew?: boolean;
  icon?: React.ReactNode;
};

const Header = () => {
  const dispatch = useAppDispatch();
  const { showHeader } = useScrollHeader();
  const { pathname, locale = 'uz' } = useRouter();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const userData = useAppSelector(state => state.user);
  const isLoading = useAppSelector(state => state.user.loading);
  const t = globalConst.extraTranslation.header[locale as 'uz' | 'ru'];

  useEffect(() => {
    dispatch(resetRedirectUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navItems: NavItemType[] = [
    { label: t.bosh_sahifa, href: '/' },
    { label: t.mavzuli_test, href: globalConst.pageNames.TOPIC_TEST },
    { label: t.blok_test, href: globalConst.pageNames.BLOCK_TEST },
    { label: t.oliygohlar, href: globalConst.pageNames.UNIVERSITIES },
    { label: t.yangiliklar, href: globalConst.pageNames.NEWS },
    // { label: t.xususiy_universitetlar, href: globalConst.pageNames.PRIVATE_UNIVERSITIES },
    // { label: t.courses, href: globalConst.pageNames.COURSES }
  ];

  return (
    <header
      className={classNames('fixed top-0  z-50 w-full bg-white shadow-sm transition-transform duration-300', {
        'pointer-events-auto translate-y-0': showHeader,
        'pointer-events-none -translate-y-full': !showHeader
      })}
    >
      <MobileDrawer navItems={navItems} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="max-w-screen container mx-auto flex min-h-14 items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <div
            role="button"
            aria-label="Open mobile menu"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setDrawerOpen(true)}
            onClick={() => setDrawerOpen(true)}
            className="flex cursor-pointer lg:hidden"
          >
            <HamburgerIcon />
          </div>
          <Link href="/" aria-label="Go to homepage" className="flex items-center space-x-2 text-xl font-bold">
            <AkamLogoCheckIcon aria-label="Akam Logo" />
            <div className="hidden sm:block">
              <AkamLogoTextIcon aria-label="Akam logo text" />
            </div>
          </Link>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          {!userData?.premium ? (
            <Link
              onClick={() => onTrackYandexGoal(globalConst.yandexGoalKeys.header_pro_button)}
              href={globalConst.pageNames.PRO}
            >
              <div>
                <div className="from-green-450 to-blue-450 z-0 flex items-center justify-center rounded-[11px] bg-gradient-to-tl p-[2px]">
                  <div className="shine-button from-green-450 to-blue-450 relative z-10 rounded-[9px] bg-gradient-to-br py-1 pl-2 pr-3 text-sm font-medium text-white">
                    <div className="relative z-10 flex items-center gap-1.5">
                      <AkamCheckIcon /> <span className="hidden sm:block">Akam</span> Pro
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ) : null}
          <div className="hidden lg:block">
            <LanguageDropdown />
          </div>
          <div className="lg:hidden">
            <Tooltip title={t.xatolar_va_takliflar}>
              <Feedback />
            </Tooltip>
          </div>
          <Visible
            if={isLoading}
            else={
              <Visible
                if={userData?.phone}
                else={
                  <button
                    onClick={() => dispatch(openAuthModal())}
                    aria-label={t.kirish || 'Kirish'}
                    className="sm:w-24.5 flex h-9 w-12 cursor-pointer items-center justify-center rounded-lg bg-green-500 px-3 text-white sm:space-x-2"
                  >
                    <LoginIcon className="h-5 w-5 sm:h-4 sm:w-4" />
                    <span className="hidden sm:block">{t.kirish}</span>
                  </button>
                }
              >
                <ProfileDropDown
                  userName={`${userData.name.slice(0, 1)}${userData?.last_name !== null ? userData?.last_name.slice(0, 1) : ''}`}
                  userImage={userData.image}
                />
              </Visible>
            }
          >
            <div className="w-24.5 hidden h-9 animate-pulse rounded-lg bg-gray-200 sm:block" />
          </Visible>
        </div>
      </div>
      <hr className="!border-.5 !my-1 hidden !border-gray-200 lg:flex" />
      <div className="mx-auto hidden max-w-[1042px] items-center justify-between lg:flex">
        <ul className="hidden space-x-6 px-3 py-2 text-gray-800 lg:flex">
          {navItems.map(item => {
            const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);

            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className={`relative flex items-center gap-2 ${isActive ? 'font-medium text-green-600' : 'hover:text-green-600'}`}
                >
                  <span className="flex items-center gap-1 text-[13px]">
                    {item?.icon && item?.icon}
                    {item.label}
                    {item.isNew && (
                      <span className="relative -top-2 right-0.5 flex items-center justify-center rounded-full bg-gray-800 px-1.5 py-0.5 text-[0.625rem] font-medium text-white">
                        {t.yangi}
                      </span>
                    )}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
        <Feedback text={t.xatolar_va_takliflar} />
      </div>
    </header>
  );
};

export default Header;
